import { useUpdateVetterEntityMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectPopover } from '../../components/ui/select-popover';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';

/** Sentinel for "no primary contact" — the mutation receives null. */
const NO_CONTACT = '';

export interface VetterMemberOption {
	id: string;
	firstName: string;
	lastName: string;
}

export interface VetterData {
	id: string;
	name: string;
	description?: string | null;
	notes?: string | null;
	isActive: boolean;
	primaryContactVetterUserId?: string | null;
	/** Members of this entity — the only valid primary-contact choices. */
	members?: VetterMemberOption[];
}

export function EditVetterModal({
	open,
	onOpenChange,
	vetter,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	vetter: VetterData | null;
	onSuccess?: () => void;
}) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [notes, setNotes] = useState('');
	const [primaryContactId, setPrimaryContactId] = useState<string>(NO_CONTACT);
	const [isActive, setIsActive] = useState(true);

	const [updateVetterEntity, { loading }] = useUpdateVetterEntityMutation();

	const members = vetter?.members ?? [];

	useEffect(() => {
		if (open && vetter) {
			setName(vetter.name);
			setDescription(vetter.description || '');
			setNotes(vetter.notes || '');
			setPrimaryContactId(vetter.primaryContactVetterUserId || NO_CONTACT);
			setIsActive(vetter.isActive);
		}
	}, [open, vetter]);

	const handleSubmit = async () => {
		if (!vetter || !name.trim()) return;

		// The selector is only rendered when the entity has members, so with none
		// we omit the field entirely rather than sending a null that would clear
		// a primary contact the admin never saw. When it is rendered, the options
		// come from the member list, which is exactly what the backend accepts.
		const nextPrimaryContact = members.some((m) => m.id === primaryContactId)
			? primaryContactId
			: null;

		try {
			const { data } = await updateVetterEntity({
				variables: {
					id: vetter.id,
					input: {
						name: name.trim(),
						// null (not undefined) so emptying a field actually clears it
						description: description.trim() || null,
						notes: notes.trim() || null,
						primaryContactVetterUserId:
							members.length > 0 ? nextPrimaryContact : undefined,
						isActive,
					},
				},
			});

			if (data?.updateVetterEntity?.success) {
				toast.success('Vetter updated successfully');
				onOpenChange(false);
				onSuccess?.();
			} else {
				toast.error(data?.updateVetterEntity?.message || 'Failed to update vetter');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update vetter');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit vetter</DialogTitle>
					<DialogDescription>Update vetter entity details.</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-vetter-name">
									Name <span className="text-destructive">*</span>
								</Label>
								<Input
									id="edit-vetter-name"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Quality Inspections Ltd"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Primary contact</Label>
								{members.length === 0 ? (
									<div className="rounded-lg border border-line-2 px-3 py-2 text-sm text-text-4">
										Invite a team member first — the primary contact has to be
										one of them.
									</div>
								) : (
									<SelectPopover
										value={primaryContactId}
										onChange={setPrimaryContactId}
										placeholder="No primary contact"
										options={[
											{ value: NO_CONTACT, label: 'No primary contact' },
											...members.map((m) => ({
												value: m.id,
												label: `${m.firstName} ${m.lastName}`,
											})),
										]}
									/>
								)}
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-vetter-description">Description</Label>
								<Textarea
									id="edit-vetter-description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Brief description of this vetter entity"
									rows={3}
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-vetter-notes">Notes</Label>
								<Textarea
									id="edit-vetter-notes"
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="Anything worth remembering about this vetter"
									rows={3}
								/>
							</div>

							<div className="flex items-center justify-between rounded-lg border border-line-2 px-3 py-2.5">
								<div>
									<Label htmlFor="edit-vetter-active" className="text-sm">
										Active
									</Label>
									<p className="text-sm text-muted-foreground">
										Mark this vetter as active or inactive
									</p>
								</div>
								<Switch
									id="edit-vetter-active"
									checked={isActive}
									onCheckedChange={setIsActive}
								/>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || loading}>
							{loading ? 'Saving…' : 'Save changes'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
